import { Injectable } from '@angular/core';
import { Observable, Subject, filter } from 'rxjs';

import { CacheEvent, EventFilter } from '../models/cache-models';

/**
 * Central event bus for all cache-related events.
 * Simplifies communication between different cache components.
 */
@Injectable({
  providedIn: 'root'
})
export class CacheEventBus {
  private events = new Subject<CacheEvent<unknown>>();

  /**
   * Dispatch an event to all listeners.
   *
   * @param event The cache event to dispatch.
   */
  public dispatch(event: CacheEvent<unknown>): void {
    this.events.next(event);
  }

  /**
   * Listen for events, optionally filtered.
   *
   * @param eventFilter Optional filter to limit which events are received.
   * @returns Observable of cache events matching the filter.
   */
  public listen(eventFilter?: EventFilter): Observable<CacheEvent<unknown>> {
    if (!eventFilter) {
      return this.events.asObservable();
    }

    return this.events.pipe(
      filter(event => this.matchesFilter(event, eventFilter))
    );
  }

  /**
   * Check if an event matches the provided filter.
   *
   * @param event The cache event to check.
   * @param filter The filter to apply.
   * @returns True if the event matches the filter.
   */
  private matchesFilter(event: CacheEvent<unknown>, filter: EventFilter): boolean {
    // Check each filter property if it exists
    if (filter.storeId && event.storeId !== filter.storeId) {
      return false;
    }

    if (filter.projectId && event.projectId !== filter.projectId) {
      return false;
    }

    if (filter.type && event.type !== filter.type) {
      return false;
    }

    return true;
  }
}
